// MissionTaskChecklistLinkModal.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useBase } from 'context/BaseContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getSingularLabel } from 'utilities/Languages';
import { createNode as createNodeFromType } from 'utilities/NodeFactory';
import { resolveCreateNodeModule } from 'utilities/createNodeModuleContext';

// Services
import { createNode as createNodeDoc } from 'services/nodes/NodeWriteService';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import TextInput from 'components/textinput/TextInput';

export const CHECKLIST_LINK_TYPE_KEY = 'LINK';

/**
 * Normalize a typed URL for LINK resources.
 *
 * @param {string} raw
 * @returns {string}
 */
function normalizeLinkUrl(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * MissionTaskChecklistLinkModal
 *
 * Title + URL create for checklist Resource Link entries (no parent edge yet —
 * the recipe stores the nodeKey; mint/runtime attaches to the TASK).
 *
 * @param {boolean} isOpen
 * @param {function} setOpen
 * @param {function} [onCreated]
 * @returns {ReactNode}
 */
const MissionTaskChecklistLinkModal = React.memo(({
  isOpen,
  setOpen,
  onCreated,
}) => {
  const { activeBase } = useBase();
  const { getType } = useNodeTypes();
  const { user } = useUser();
  const isMobile = useIsMobile();

  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [titleError, setTitleError] = useState('');
  const [urlError, setUrlError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const typeDef = useMemo(
    () => getType(CHECKLIST_LINK_TYPE_KEY) || null,
    [getType],
  );

  const singularLabel = useMemo(() => (
    getSingularLabel(typeDef, CHECKLIST_LINK_TYPE_KEY, 'en') || 'Link'
  ), [typeDef]);

  const baseKey = String(activeBase?.key || '').trim();

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setUrl('');
      setTitleError('');
      setUrlError('');
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (isSaving || !user?.key) return;

    const trimmedTitle = String(title || '').trim();
    const normalizedUrl = normalizeLinkUrl(url);
    let hasError = false;

    if (!trimmedTitle) {
      setTitleError('Title is required.');
      hasError = true;
    } else {
      setTitleError('');
    }

    if (!normalizedUrl) {
      setUrlError('URL is required.');
      hasError = true;
    } else {
      setUrlError('');
    }

    if (!baseKey) {
      setUrlError('No active space.');
      hasError = true;
    }

    if (!typeDef) {
      setUrlError('LINK type is not available.');
      hasError = true;
    }

    if (hasError) return;

    setIsSaving(true);

    try {
      const newNode = await createNodeFromType(
        CHECKLIST_LINK_TYPE_KEY,
        user,
        baseKey,
        { module: resolveCreateNodeModule({ consume: true }) },
      );
      newNode.title = trimmedTitle;

      const dataKey = String(typeDef?.dataSection || '').trim()
        || `data_${CHECKLIST_LINK_TYPE_KEY.toLowerCase()}`;
      const existingData = newNode[dataKey] && typeof newNode[dataKey] === 'object'
        ? { ...newNode[dataKey] }
        : {};
      newNode[dataKey] = {
        ...existingData,
        url: normalizedUrl,
      };

      await createNodeDoc(newNode.key, newNode);

      setOpen?.(false);
      onCreated?.(newNode);
      toast.success('Link added.');
    } catch (error) {
      console.error('[MissionTaskChecklistLinkModal] Failed to save LINK:', error);
      toast.error(`Could not save: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      iconKey="link"
      isOpen={isOpen}
      title={`Add ${singularLabel}`}
      width={isMobile ? '100%' : 420}
      height="auto"
      onClose={() => {
        if (isSaving) return;
        setOpen?.(false);
      }}
    >
      <ModalForm>
        <ModalField label="Title">
          <TextInput
            autoFocus
            autoSelect
            error={!!titleError}
            helperText={titleError}
            maxLength={160}
            placeholder="Title"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              if (titleError) setTitleError('');
            }}
            onEnterPress={handleSave}
            onEscapePress={() => {
              if (isSaving) return;
              setOpen?.(false);
            }}
          />
        </ModalField>

        <ModalField label="URL">
          <TextInput
            error={!!urlError}
            helperText={urlError}
            maxLength={2000}
            placeholder="https://"
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
              if (urlError) setUrlError('');
            }}
            onEnterPress={handleSave}
            onEscapePress={() => {
              if (isSaving) return;
              setOpen?.(false);
            }}
          />
        </ModalField>
      </ModalForm>

      <ModalButtons>
        <Button
          enabled={!isSaving}
          height="44px"
          label="Cancel"
          onClick={() => setOpen?.(false)}
        />
        <Button
          enabled={!isSaving}
          height="44px"
          label={isSaving ? 'Saving...' : 'Save'}
          onClick={handleSave}
        />
      </ModalButtons>
    </Modal>
  );
});

MissionTaskChecklistLinkModal.displayName = 'MissionTaskChecklistLinkModal';

export default MissionTaskChecklistLinkModal;
